<template>
    <b-overlay :show="isLoading" class="rounded" opacity="0.2">
        <b-card no-body>
            <b-card-body>

                <div v-if="editing" class="alert alert-warning d-flex align-items-center py-50 mb-1">
                    <feather-icon icon="EditIcon" size="16" class="mr-50" />
                    <span>
                        <strong>Düzenleme Modu:</strong> {{ form.ProcessNumber }} numaralı yakıt giriş işlemini düzenliyorsunuz.
                        Kaydet'e bastığınızda mevcut depo bakiyeleri ters çevrilip yeni miktarlar uygulanır.
                        Sipariş değişikliği için kaydı silip yeniden oluşturunuz.
                    </span>
                </div>

                <div class="row">
                    <div class="mb-2" :class="form.FuelOrderId ? 'col-md-9' : 'col-md-12'">
                        <label class="form-label">Sipariş Numarası <span class="text-danger">*</span></label>
                        <v-select-list
                            v-model="form.FuelOrderId"
                            :options="fuelOrderOptions"
                            label="displayText"
                            :reduce="(order) => order.Id"
                            placeholder="İlgili siparişi seçiniz..."
                            :loading="isLoadingOrder"
                            :disabled="editing"
                        />
                    </div>
                    <div v-if="form.FuelOrderId" class="col-md-3 mb-2">
                        <label class="form-label">İşlem Numarası</label>
                        <input type="text" class="form-control" v-model="form.ProcessNumber" disabled />
                    </div>

                    <div v-if="form.FuelOrderId" class="col-12">
                        <label class="form-label" v-if="!isAdBlue">
                            Mühür Numaraları (11 Adet)
                            <small class="text-muted">- 7 haneli barkod veya elle giriş</small>
                        </label>
                        <div class="row">
                            <div v-for="(seal, index) in sealNumbers" :key="index" class="col-md-4 mb-2">
                                <div class="input-group input-group-sm">
                                    <div class="input-group-prepend">
                                        <span class="input-group-text">{{ index + 1 }}</span>
                                    </div>
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="seal.value"
                                        placeholder="7 haneli mühür no"
                                        maxlength="7"
                                        @input="onSealInput(index)"
                                        :disabled="isAdBlue"
                                    />
                                    <div class="input-group-append">
                                        <button
                                            class="btn btn-outline-info"
                                            type="button"
                                            @click="openBarcodeScanner(index)"
                                            :disabled="isAdBlue"
                                            title="Barkod Oku"
                                        >
                                            <feather-icon icon="CameraIcon" size="14" />
                                        </button>
                                        <button
                                            class="btn btn-outline-danger"
                                            type="button"
                                            @click="removeSeal(index)"
                                            :disabled="sealNumbers.length <= 1 || isAdBlue"
                                            title="Sil"
                                        >
                                            <feather-icon icon="TrashIcon" size="14" />
                                        </button>
                                        <button
                                            class="btn btn-outline-primary"
                                            type="button"
                                            @click="addSeal"
                                            v-if="index === sealNumbers.length - 1 && canAddMoreSeals"
                                            :disabled="!isCurrentSealValid(index) || isAdBlue"
                                            title="Yeni Mühür Ekle"
                                        >
                                            <feather-icon icon="PlusIcon" size="14" />
                                        </button>
                                    </div>
                                </div>
                                <small v-if="seal.value && seal.value.length !== 7" class="text-danger"> 7 haneli olmalıdır </small>
                            </div>
                        </div>
                    </div>

                    <div v-if="selectedOrderInfo" class="col-12 mb-2 p-2 border rounded bg-light">
                        <div class="d-flex justify-content-between align-items-center border-bottom pb-1 mb-2">
                            <h5 class="mb-0">Sipariş Özeti</h5>
                            <span class="badge" :class="fuelTypeBadgeClass">{{ selectedOrderInfo.fuelTypeName }}</span>
                        </div>
                        <div class="row">
                            <div class="col-md-6">
                                <p class="mb-1"><strong>Tedarikçi:</strong> {{ selectedOrderInfo.supplierName }}</p>
                                <p class="mb-0"><strong>Şube:</strong> {{ selectedOrderInfo.branchName }}</p>
                            </div>
                            <div class="col-md-6">
                                <p class="mb-1"><strong>Sürücü:</strong> {{ selectedOrderInfo.driverName }}</p>
                                <div class="row">
                                    <div class="col-5">
                                        <p class="mb-0"><strong>Araç:</strong> {{ selectedOrderInfo.plateValue }}</p>
                                    </div>
                                    <div class="col-7">
                                        <p class="mb-0"><strong>Treyler:</strong> {{ selectedOrderInfo.trailerPlateValue }}</p>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6 mt-1">
                                <p class="mb-0"><strong>Sipariş Tarihi:</strong> {{ selectedOrderInfo.orderDate }}</p>
                            </div>
                            <div class="col-md-6 mt-1">
                                <p class="mb-0">
                                    <strong>Sipariş Durumu:</strong>
                                    <strong :class="statusVariantClass">{{ selectedOrderInfo.orderStatusName }}</strong>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div v-if="form.FuelOrderId" class="row w-100 mx-0">
                        <div class="col-md-3 mb-2 pl-0">
                            <label class="form-label">Sipariş Toplamı [lt]</label>
                            <VCurrencyInput :value="selectedOrderInfo ? selectedOrderInfo.orderQuantity : 0" :disabled="true" />
                        </div>
                        <div class="col-md-3 mb-2">
                            <label class="form-label">Kullanılabilir Miktar [lt]</label>
                            <VCurrencyInput :value="form.IncomingQuantity" :disabled="true" />
                        </div>
                        <div class="col-md-2 mb-2">
                            <label class="form-label">Yoğunluk <span class="text-danger">*</span></label>
                            <input type="number" class="form-control" v-model.number="form.Density" step="0.0001" @input="onDensityChange" />
                        </div>
                        <div class="col-md-2 mb-2">
                            <label class="form-label">Sipariş [ton]</label>
                            <input
                                type="text"
                                class="form-control"
                                :value="formatNumber(orderTonnage, 3)"
                                disabled
                                style="background-color: #e9ecef; font-weight: bold"
                            />
                        </div>
                        <div class="col-md-2 mb-2 pr-0">
                            <label class="form-label">Kalan [ton]</label>
                            <input
                                type="text"
                                class="form-control"
                                :value="formatNumber(availableTonnage, 3)"
                                disabled
                                style="background-color: #e9ecef; font-weight: bold"
                            />
                        </div>
                        <div class="col-12 d-flex justify-content-between align-items-center"><h5 class="mb-1">Depo Dağıtımları</h5></div>
                        <div v-for="(dist, index) in form.FuelStorageTransactionDetails" :key="index" class="col-12">
                            <div class="row align-items-end p-1 mb-2 border rounded position-relative">
                                <div class="col-md-12 mb-1">
                                    <label class="form-label">Depo <span class="text-danger">*</span></label>
                                    <v-validator :errors="errors" :parameter="`dist_${index}_FuelStorageId`" :watch="dist.FuelStorageId">
                                        <v-select
                                            v-model="dist.FuelStorageId"
                                            :options="filteredStorages"
                                            :reduce="(item) => item.Id"
                                            label="DisplayName"
                                            placeholder="Seçiniz..."
                                            @input="onDepotChange(dist)"
                                        />
                                    </v-validator>
                                    <div
                                        v-if="dist.FuelStorageId && getDepotInfo(dist.FuelStorageId)"
                                        class="d-flex align-items-center"
                                        style="padding-top: 0.4rem"
                                    >
                                        <div class="mr-2">
                                            <strong class="font-weight-bold text-secondary">Toplam Kapasite:</strong>
                                            <span class="badge badge-light-secondary ml-50"
                                                >{{ (getDepotInfo(dist.FuelStorageId).Capacity || 0).toLocaleString("tr-TR") }} lt</span
                                            >
                                        </div>
                                        <div>
                                            <strong class="font-weight-bold text-success">Boş Kapasite:</strong>
                                            <span class="badge badge-light-success ml-50"
                                                >{{
                                                    (
                                                        getDepotInfo(dist.FuelStorageId).Capacity - getDepotInfo(dist.FuelStorageId).FuelQuantity
                                                    ).toLocaleString("tr-TR")
                                                }}
                                                lt</span
                                            >
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-3 mb-1">
                                    <label class="form-label">Kantar Ölçümü [ton] <span class="text-danger">*</span></label>
                                    <v-validator :errors="errors" :parameter="`dist_${index}_Tonnage`" :watch="dist.Tonnage">
                                        <VCurrencyInput
                                            :value="dist.Tonnage"
                                            @update-value="updateTonnage($event, dist)"
                                            :fractionDigits="4"
                                            placeholder="Tonaj giriniz..."
                                        />
                                    </v-validator>
                                </div>
                                <div class="col-md-3 mb-1">
                                    <label class="form-label">Miktar [lt] <span class="text-danger">*</span></label>
                                    <v-validator :errors="errors" :parameter="`dist_${index}_Quantity`" :watch="dist.Quantity">
                                        <input
                                            type="text"
                                            class="form-control"
                                            :value="formatNumber(dist.Quantity)"
                                            disabled
                                            style="background-color: #f8f9fa; font-weight: bold"
                                        />
                                    </v-validator>
                                </div>
                                <div class="col-md-4 mb-1">
                                    <label class="form-label">Teslim Alan <span class="text-danger">*</span></label>
                                    <v-validator :errors="errors" :parameter="`dist_${index}_ReceivingEmployeeId`" :watch="dist.ReceivingEmployeeId">
                                        <v-select-list
                                            v-model="dist.ReceivingEmployeeId"
                                            :options="getSupervisorOptions(dist.FuelStorageId)"
                                            label="EmployeeName"
                                            :reduce="(emp) => emp.EmployeeId"
                                            placeholder="Depo sorumlusu seçin..."
                                        />
                                    </v-validator>
                                </div>
                                <div class="col-md-2 mb-1 d-flex">
                                    <button
                                        type="button"
                                        class="btn btn-outline-danger me-1 px-1"
                                        @click="removeDistribution(index)"
                                        :disabled="form.FuelStorageTransactionDetails.length <= 1"
                                    >
                                        <feather-icon icon="TrashIcon" size="16" />
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-outline-primary ml-1 px-1 p"
                                        @click="addDistribution"
                                        v-if="index === form.FuelStorageTransactionDetails.length - 1"
                                    >
                                        <feather-icon icon="PlusIcon" size="16" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 mb-2 px-0">
                            <div class="text-end p-0 rounded" :style="summaryStyle">
                                <h5
                                    class="mb-0 text-right font-weight-bolder"
                                    style="padding: 0.3rem; font-size: small"
                                    :class="remainingTonnage >= 0 ? 'text-success' : 'text-danger'"
                                >
                                    Kalan Dağıtım Miktarı: {{ formatNumber(remainingTonnage, 3) }} ton ({{ formatNumber(remainingFuelQuantity) }} lt)
                                </h5>
                            </div>
                        </div>
                        <div v-if="isOverDistributed" class="col-12 mb-0 px-0">
                            <div class="alert alert-danger p-1 text-center" role="alert">
                                <feather-icon icon="AlertTriangleIcon" class="me-1"></feather-icon>
                                Dağıtılan toplam tonaj ({{ totalDistributedTonnage.toFixed(3) }} ton), kalan miktarı ({{
                                    remainingTonnage.toFixed(3)
                                }}
                                ton) aşamaz!
                            </div>
                        </div>
                        <div v-if="isCapacityExceeded" class="col-12 mb-0 px-0">
                            <div class="alert alert-danger p-1 text-center" role="alert">
                                <feather-icon icon="AlertTriangleIcon" class="me-1"></feather-icon>Aktarılan miktar, seçilen deponun boş kapasitesini
                                aşamaz!
                            </div>
                        </div>
                    </div>

                    <div v-else class="col-12 text-center p-3">
                        <p class="text-muted">İşleme devam etmek için lütfen bir sipariş seçiniz.</p>
                    </div>
                </div>
            </b-card-body>
            <b-card-footer>
                <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-outline-secondary" v-on:click="resetForm">Temizle</button>
                    <button type="button" class="btn btn-primary ml-1" v-on:click="save" :disabled="isSaveDisabled">Kaydet</button>
                </div>
            </b-card-footer>
        </b-card>

        <BarcodeScannerModal @code-scanned="onBarcodeScanned" />
    </b-overlay>
</template>

<script>
import FuelStorageApi from "@/api/fuel-storage";
import EmployeeOperationsApi from "@/api/employee-operations";
import { BOverlay, BCard, BCardBody, BCardFooter, BModal, VBTooltip } from "bootstrap-vue";
import VCurrencyInput from "@/components/VCurrencyInput.vue";
import VValidator from "@/components/VValidator.vue";
import BarcodeScannerModal from "@/components/BarcodeScannerModal.vue";

export default {
    name: "FuelFillingPage",
    components: {
        BOverlay,
        BCard,
        BCardBody,
        BCardFooter,
        VCurrencyInput,
        BModal,
        VValidator,
        BarcodeScannerModal,
    },
    data() {
        return {
            isLoading: false,
            isLoadingOrder: false,
            editing: false,
            isLoadingForEdit: false,
            form: {},
            initialForm: {},
            errors: {},
            fuelOrderOptions: [],
            fuelStorageOptions: [],
            allEmployees: [],
            selectedOrderInfo: null,
            selectedFuelTypeId: null,
            sealNumbers: [{ value: "" }],
            activeSealIndex: null,
        };
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    created() {
        this.initializeForm();
        const transactionId = this.$route && this.$route.query ? this.$route.query.transactionId : null;
        if (transactionId) {
            // Düzenleme modu: önce dropdown verilerini yükle, sonra işlemi çek ve formu doldur.
            this.fetchInitialData().then(() => this.loadForEdit(transactionId));
        } else {
            this.fetchInitialData();
        }
    },
    watch: {
        "form.FuelOrderId"(newOrderId) {
            // Düzenleme modunda form zaten sunucudan doldurulduğu için sıfırlama yapma.
            if (this.isLoadingForEdit || this.editing) return;
            if (newOrderId) {
                this.onOrderSelect(newOrderId);
            } else {
                this.initializeForm();
                this.selectedOrderInfo = null;
                this.selectedFuelTypeId = null;
            }
        },
        selectedOrderInfo(newVal) {
            if (newVal && newVal.fuelTypeName) {
                const newDensity = this.getDefaultDensity();
                this.form.FuelStorageTransactionDetails.forEach((dist) => {
                    if (!dist.Density || dist.Density === 0.8314 || dist.Density === 1.09) {
                        dist.Density = newDensity;
                        this.calculateQuantity(dist);
                    }
                });
            }
        },
    },
    computed: {
        isAdBlue() {
            if (!this.selectedOrderInfo || !this.selectedOrderInfo.fuelTypeName) return false;
            return this.selectedOrderInfo.fuelTypeName.toLowerCase().includes("adblue");
        },
        canAddMoreSeals() {
            return this.sealNumbers.length < 11;
        },

        isSaveDisabled() {
            if (!this.form || !this.form.FuelOrderId || !this.form.FuelStorageTransactionDetails) return true;
            const totalDistributed = this.form.FuelStorageTransactionDetails.reduce((sum, dist) => sum + (Number(dist.Quantity) || 0), 0);
            const hasValidSeal =
                this.isAdBlue ||
                (this.sealNumbers &&
                    this.sealNumbers.length === 11 &&
                    this.sealNumbers.every((s) => s.value && s.value.toString().trim().length === 7));
            return !this.form.FuelOrderId || totalDistributed <= 0 || this.isOverDistributed || this.isCapacityExceeded || !hasValidSeal;
        },

        remainingFuelQuantity() {
            if (!this.form || !this.form.FuelStorageTransactionDetails) return 0;
            const totalIncoming = Number(this.form.IncomingQuantity) || 0;
            const totalDistributed = this.form.FuelStorageTransactionDetails.reduce((sum, dist) => sum + (Number(dist.Quantity) || 0), 0);
            return totalIncoming - totalDistributed;
        },
        summaryStyle() {
            const style = { transition: "background-color 0.3s ease" };
            style.backgroundColor = this.remainingTonnage >= 0 ? "#d1e7dd" : "#f8d7da";
            return style;
        },
        orderTonnage() {
            if (!this.selectedOrderInfo || !this.form.Density) return 0;
            const liters = this.selectedOrderInfo.orderQuantity || 0;
            return parseFloat(((liters * this.form.Density) / 1000).toFixed(3));
        },

        availableTonnage() {
            if (!this.form.IncomingQuantity || !this.form.Density) return 0;
            return parseFloat(((this.form.IncomingQuantity * this.form.Density) / 1000).toFixed(3));
        },

        totalDistributedTonnage() {
            if (!this.form.FuelStorageTransactionDetails) return 0;
            return this.form.FuelStorageTransactionDetails.reduce((sum, dist) => sum + (Number(dist.Tonnage) || 0), 0);
        },

        remainingTonnage() {
            return parseFloat((this.availableTonnage - this.totalDistributedTonnage).toFixed(3));
        },

        isOverDistributed() {
            return this.totalDistributedTonnage > this.availableTonnage;
        },
        isCapacityExceeded() {
            const depotTotals = new Map();

            this.form.FuelStorageTransactionDetails.forEach((dist) => {
                if (dist.FuelStorageId && dist.Quantity) {
                    const currentTotal = depotTotals.get(dist.FuelStorageId) || 0;
                    depotTotals.set(dist.FuelStorageId, currentTotal + dist.Quantity);
                }
            });

            for (const [storageId, totalQuantity] of depotTotals) {
                const depot = this.getDepotInfo(storageId);
                if (depot) {
                    const availableCapacity = depot.Capacity - depot.FuelQuantity;
                    if (totalQuantity > availableCapacity) return true;
                }
            }

            return false;
        },
        statusVariantClass() {
            if (!this.selectedOrderInfo) return "text-muted";
            const map = { 0: "text-info", 1: "text-warning", 2: "text-secondary", 3: "text-success", 4: "text-danger" };
            return map[this.selectedOrderInfo.orderStatusId] || "text-muted";
        },
        fuelTypeBadgeClass() {
            if (!this.selectedOrderInfo) return "bg-secondary";
            const fuelType = this.selectedOrderInfo.fuelTypeName || "";
            if (fuelType.toLowerCase().includes("adblue")) return "bg-primary";
            if (fuelType.toLowerCase().includes("motorin")) return "bg-warning";
            return "bg-secondary";
        },
        supervisorEmployeeMap() {
            return new Map(this.allEmployees.map((emp) => [emp.Id, emp.FullName]));
        },
        filteredStorages() {
            if (!this.selectedFuelTypeId) return [];
            return this.fuelStorageOptions
                .filter((storage) => storage.FuelStorageFuelTypeId === this.selectedFuelTypeId)
                .map((storage) => ({
                    ...storage,
                    DisplayName: `${storage.Name} [${(storage.Capacity || 0).toLocaleString("tr-TR")} lt]`,
                }));
        },
    },
    methods: {
        clearOnFocus(distribution) {
            if (distribution.Quantity === 0) {
                distribution.Quantity = null;
            }
        },
        setOnBlur(distribution) {
            if (distribution.Quantity === null || distribution.Quantity === "") {
                distribution.Quantity = 0;
            }
        },
        initializeForm() {
            const now = new Date();
            const timestamp = now.getTime().toString().slice(-8);
            const processNumber = `DG${timestamp}`;
            this.form = {
                ProcessNumber: processNumber,
                FuelTransactionType: 1,
                IncomingQuantity: 0,
                Description: "",
                Density: 0.8314,
                FuelStorageTransactionDetails: [
                    {
                        FuelOrderId: null,
                        FuelStorageId: null,
                        Quantity: 0,
                        Tonnage: null,
                        IsOrderForceClose: false,
                        DriverEmployeeId: null,
                        ReceivingEmployeeId: null,
                    },
                ],
            };
            this.initialForm = JSON.parse(JSON.stringify(this.form));
            this.sealNumbers = [{ value: "" }];
        },
        resetForm() {
            this.initializeForm();
            this.selectedOrderInfo = null;
            this.selectedFuelTypeId = null;
            this.errors = {};
        },
        validate() {
            this.errors = {};

            if (!this.form.FuelOrderId) {
                this.$set(this.errors, "FuelOrderId", "Sipariş seçmek zorunludur");
            }

            this.form.FuelStorageTransactionDetails.forEach((dist, index) => {
                if (!dist.FuelStorageId) {
                    this.$set(this.errors, `dist_${index}_FuelStorageId`, "Depo seçmek zorunludur");
                }
                if (!dist.Quantity || dist.Quantity <= 0) {
                    this.$set(this.errors, `dist_${index}_Quantity`, "Geçerli bir miktar hesaplanamadı");
                    this.$set(this.errors, `dist_${index}_Tonnage`, "Kantar ölçümü girilmelidir");
                }
                if (!dist.Density || dist.Density <= 0) {
                    this.$set(this.errors, `dist_${index}_Density`, "Yoğunluk girilmelidir");
                }
                if (!dist.ReceivingEmployeeId) {
                    this.$set(this.errors, `dist_${index}_ReceivingEmployeeId`, "Teslim alan zorunludur");
                }
            });

            return Object.keys(this.errors).length === 0;
        },
        fetchInitialData() {
            this.isLoading = true;
            return Promise.all([this.loadAllEmployees(), this.loadFuelOrderOptions(), this.loadFuelStorageOptions()])
                .catch((err) => this.$result(null, "Dropdown seçenekleri yüklenirken bir hata oluştu: " + err))
                .finally(() => (this.isLoading = false));
        },
        loadForEdit(transactionId) {
            this.isLoading = true;
            this.isLoadingForEdit = true;
            return FuelStorageApi.FuelStorageTransaction.Get(transactionId)
                .then((res) => {
                    if (!res.data || !res.data.Success) {
                        this.$result(res);
                        return;
                    }
                    const tx = res.data.Data;
                    if (tx.FuelTransactionType !== 1) {
                        this.$result(null, "Bu kayıt bu sayfadan düzenlenemez (tip uyumsuz).");
                        return;
                    }
                    const details = tx.FuelStorageInputTransactionDetails || [];
                    const firstDetail = details[0] || {};
                    const fuelOrderId = firstDetail.FuelOrderId || null;
                    const density = firstDetail.Density || 0.8314;

                    // Formu doğrudan set et (watcher isLoadingForEdit sayesinde sıfırlama yapmaz).
                    this.form = {
                        Id: tx.Id,
                        ProcessNumber: tx.ProcessNumber,
                        FuelTransactionType: 1,
                        IncomingQuantity: tx.IncomingQuantity || 0,
                        Description: tx.Description || "",
                        Density: density,
                        FuelOrderId: fuelOrderId,
                        FuelStorageTransactionDetails: details.map((d) => ({
                            Id: d.Id,
                            FuelOrderId: d.FuelOrderId,
                            FuelStorageId: d.FuelStorageId,
                            Quantity: d.Quantity,
                            Tonnage: d.Tonnage,
                            Density: d.Density || density,
                            IsOrderForceClose: false,
                            DriverEmployeeId: d.DriverEmployeeId,
                            ReceivingEmployeeId: d.ReceivingEmployeeId,
                            VehiclePlateId: d.VehiclePlateId,
                        })),
                    };

                    // Mühür numaralarını Description'dan parse et (virgülle ayrılmış, her biri 7 haneli).
                    if (tx.Description) {
                        const parsed = tx.Description.split(",")
                            .map((s) => s.trim())
                            .filter((s) => s.length > 0);
                        this.sealNumbers = parsed.length > 0 ? parsed.map((v) => ({ value: v })) : [{ value: "" }];
                    } else {
                        this.sealNumbers = [{ value: "" }];
                    }

                    // Sipariş özet paneli için selectedOrderInfo'yu doldur (mevcut seçeneklerden).
                    if (fuelOrderId) {
                        this.onOrderSelect(fuelOrderId, true);
                    }

                    this.editing = true;
                })
                .catch((err) => this.$result(null, err))
                .finally(() => {
                    this.isLoading = false;
                    // Watcher'ın bir sonraki tick'te reset yapmaması için flag'i bir tick sonra kapat.
                    this.$nextTick(() => {
                        this.isLoadingForEdit = false;
                    });
                });
        },
        loadAllEmployees() {
            return EmployeeOperationsApi.Employee.List().then((res) => {
                if (res.data.Success) {
                    this.allEmployees = res.data.Data || [];
                }
            });
        },
        loadFuelOrderOptions() {
            return FuelStorageApi.FuelOrder.List(true).then((res) => {
                if (res.data.Success) {
                    const today = new Date();
                    today.setHours(23, 59, 59, 999);

                    this.fuelOrderOptions = (res.data.Data || [])
                        .filter((order) => {
                            const orderDate = new Date(order.OrderDate);
                            return orderDate <= today;
                        })
                        .map((order) => ({
                            ...order,
                            displayText: `${order.OrderNumber} - ${order.FuelOrderBranch} (Tarih: ${this.formatOrderDate(order.OrderDate)})`,
                        }));
                }
            });
        },
        formatOrderDate(dateStr) {
            if (!dateStr) return "-";
            const date = new Date(dateStr);
            return date.toLocaleDateString("tr-TR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });
        },
        formatNumber(value, digits = 2) {
            if (value === null || value === undefined || isNaN(value)) return digits === 3 ? "0,000" : "0,00";
            return new Intl.NumberFormat("tr-TR", {
                minimumFractionDigits: digits,
                maximumFractionDigits: digits,
            }).format(value);
        },
        getDefaultDensity() {
            if (!this.selectedOrderInfo || !this.selectedOrderInfo.fuelTypeName) return 0.8314;
            const fuelType = this.selectedOrderInfo.fuelTypeName.toLowerCase();
            if (fuelType.includes("adblue")) return 1.09;
            if (fuelType.includes("motorin") || fuelType.includes("dizel")) return 0.8314;
            return 0.8314;
        },
        loadFuelStorageOptions() {
            return FuelStorageApi.FuelStorage.List({}).then((res) => {
                if (res.data.Success) {
                    this.fuelStorageOptions = res.data.Data || [];
                }
            });
        },
        save() {
            const validSeals = this.sealNumbers.map((s) => s.value.trim()).filter((s) => s.length > 0);
            this.form.Description = validSeals.join(", ");

            this.isLoading = true;

            const payload = JSON.parse(JSON.stringify(this.form));
            const firstDetail = this.form.FuelStorageTransactionDetails[0] || {};
            payload.FuelStorageTransactionDetails.forEach((detail) => {
                detail.FuelOrderId = payload.FuelOrderId;
                detail.DriverEmployeeId = detail.DriverEmployeeId || firstDetail.DriverEmployeeId;
                detail.VehiclePlateId = detail.VehiclePlateId || firstDetail.VehiclePlateId;
                detail.SupplierId = detail.SupplierId || firstDetail.SupplierId;
            });

            const wasEditing = this.editing;
            FuelStorageApi.FuelStorageTransaction.CreateOrUpdate(this.editing, payload)
                .then((res) => {
                    if (res.data.Success) {
                        this.$result(res);
                        if (wasEditing) {
                            this.$router.push({ name: "fuel-storage-transaction" });
                        } else {
                            this.resetForm();
                        }
                    } else {
                        this.$result(res);
                    }
                })
                .catch((err) => {
                    this.$result(null, err);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        onOrderSelect(orderId, isReopening = false) {
            if (isReopening) {
                const orderDetails = this.fuelOrderOptions.find((o) => o.Id === orderId);
                if (orderDetails) {
                    this.selectedFuelTypeId = orderDetails.FuelTypeId;
                    this.selectedOrderInfo = {
                        supplierName: orderDetails.Supplier || "Belirtilmemiş",
                        driverName: orderDetails.DriverEmployee || "Belirtilmemiş",
                        branchName: orderDetails.FuelOrderBranch || "Belirtilmemiş",
                        plateValue: orderDetails.VehiclePlate || "Belirtilmemiş",
                        trailerPlateValue: orderDetails.TrailerPlate || "Belirtilmemiş",
                        fuelTypeName: orderDetails.FuelTypeName || "Belirtilmemiş",
                        orderQuantity: orderDetails.OrderQuantity,
                        orderStatusName: orderDetails.OrderStatusName,
                        orderStatusId: orderDetails.OrderStatus,
                        orderDate: new Date(orderDetails.OrderDate).toLocaleDateString("tr-TR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                        }),
                    };
                }
                return;
            }
            this.isLoadingOrder = true;
            this.selectedOrderInfo = null;
            FuelStorageApi.FuelOrder.Get(orderId)
                .then((res) => {
                    if (!res.data.Success) throw new Error(res.data.Message || "Sipariş detayı alınamadı.");
                    const orderDetails = res.data.Data;
                    this.form.Density = orderDetails.FuelTypeName?.toLowerCase().includes("adblue") ? 1.09 : 0.8314;
                    this.selectedFuelTypeId = orderDetails.FuelTypeId;
                    this.selectedOrderInfo = {
                        supplierName: orderDetails.Supplier || "Belirtilmemiş",
                        driverName: orderDetails.DriverEmployee || "Belirtilmemiş",
                        branchName: orderDetails.FuelOrderBranch || "Belirtilmemiş",
                        plateValue: orderDetails.VehiclePlate || "Belirtilmemiş",
                        trailerPlateValue: orderDetails.TrailerPlate || "Belirtilmemiş",
                        fuelTypeName: orderDetails.FuelTypeName || "Belirtilmemiş",
                        orderQuantity: orderDetails.OrderQuantity,
                        orderStatusName: orderDetails.OrderStatusName,
                        orderStatusId: orderDetails.OrderStatus,
                        orderDate: new Date(orderDetails.OrderDate).toLocaleDateString("tr-TR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                        }),
                    };
                    this.form.ProcessNumber = orderDetails.ProcessNumber || this.form.ProcessNumber;
                    this.form.IncomingQuantity = orderDetails.RemainingOrderQuantity;
                    this.form.FuelStorageTransactionDetails = [
                        {
                            FuelOrderId: orderDetails.Id,
                            FuelStorageId: null,
                            Quantity: 0,
                            Tonnage: null,
                            Density: this.form.Density,
                            IsOrderForceClose: false,
                            DriverEmployeeId: orderDetails.DriverEmployeeId,
                            ReceivingEmployeeId: null,
                            VehiclePlateId: orderDetails.VehiclePlateId,
                            SupplierId: orderDetails.SupplierId,
                        },
                    ];
                })
                .catch((err) => {
                    this.$result(null, err);
                })
                .finally(() => {
                    this.isLoadingOrder = false;
                });
        },
        addDistribution() {
            const firstDetail = this.form.FuelStorageTransactionDetails[0] || {};
            const defaultDensity = this.getDefaultDensity();

            this.form.FuelStorageTransactionDetails.push({
                FuelOrderId: this.form.FuelOrderId,
                FuelStorageId: null,
                Quantity: 0,
                Density: this.form.Density,
                Tonnage: null,
                ReceivingEmployeeId: null,
                DriverEmployeeId: firstDetail.DriverEmployeeId,
                VehiclePlateId: firstDetail.VehiclePlateId,
                SupplierId: firstDetail.SupplierId,
                IsOrderForceClose: false,
            });
        },
        removeDistribution(index) {
            if (this.form.FuelStorageTransactionDetails.length > 1) {
                this.form.FuelStorageTransactionDetails.splice(index, 1);
            }
        },
        getSupervisorOptions(storageId) {
            if (!storageId) return [];
            const storage = this.fuelStorageOptions.find((s) => s.Id === storageId);
            if (!storage || !storage.Supervisors) return [];
            return storage.Supervisors.filter((s) => !s.Deleted).map((s) => ({
                EmployeeId: s.EmployeeId,
                EmployeeName: this.supervisorEmployeeMap.get(s.EmployeeId) || "İsim Bulunamadı",
            }));
        },
        onDepotChange(detail) {
            detail.Quantity = 0;
            detail.Tonnage = null;
            detail.ReceivingEmployeeId = null;
        },
        onDensityChange() {
            this.form.FuelStorageTransactionDetails.forEach((dist) => {
                dist.Density = this.form.Density;
                this.calculateQuantity(dist);
            });
        },
        getDepotInfo(storageId) {
            if (!storageId || !this.fuelStorageOptions) return null;
            return this.fuelStorageOptions.find((d) => d.Id === storageId);
        },
        addSeal() {
            if (this.sealNumbers.length < 11) {
                this.sealNumbers.push({ value: "" });
            }
        },
        removeSeal(index) {
            if (this.sealNumbers.length > 1) {
                this.sealNumbers.splice(index, 1);
            }
        },
        onSealInput(index) {
            let val = this.sealNumbers[index].value.replace(/[^0-9]/g, "");
            if (val.length > 7) val = val.slice(0, 7);
            this.sealNumbers[index].value = val;
        },

        isCurrentSealValid(index) {
            const val = this.sealNumbers[index].value;
            return val && val.toString().length === 7;
        },

        openBarcodeScanner(index) {
            this.activeSealIndex = index;
            this.$bvModal.show("barcode-scanner-modal");
        },

        onBarcodeScanned(code) {
            if (this.activeSealIndex !== null) {
                this.$set(this.sealNumbers[this.activeSealIndex], "value", code);
                this.activeSealIndex = null;
            }
        },

        updateTonnage(value, dist) {
            dist.Tonnage = value;
            this.calculateQuantity(dist);
        },
        calculateQuantity(dist) {
            const ton = dist.Tonnage;
            const density = this.form.Density;

            if (ton && ton > 0 && density && density > 0) {
                let rawVal = (ton * 1000) / density;

                const otherDistTotal = this.form.FuelStorageTransactionDetails.filter((d) => d !== dist).reduce(
                    (sum, d) => sum + (Number(d.Quantity) || 0),
                    0,
                );

                const maxForThisDist = this.form.IncomingQuantity - otherDistTotal;

                if (rawVal > maxForThisDist) {
                    rawVal = maxForThisDist;
                }

                dist.Quantity = parseFloat(Math.max(0, rawVal).toFixed(2));
            } else {
                dist.Quantity = 0;
            }
        },
    },
};
</script>
